/**
 * PR-A1 (Search Graft, ADO 286301; review thread 1511770): the two rendering
 * contracts' visibility rules as pure functions, one per contract.
 *
 * The `aiSearchStableList` flag is read once per mount (useAiSearch), so the
 * contracts are disjoint per mount — a render never mixes them. Keeping each
 * contract in its own function makes "what does the user see right now"
 * readable in one pass, keeps both independently unit-testable, and turns
 * flag cleanup into a deletion (drop legacyVisibility and the call-site
 * ternary) instead of an untangling.
 */
import type { AiSearchPhase } from "./types";

export interface IAiSearchVisibility {
    /** Shimmer rows while nothing is ready to show. */
    showSkeleton: boolean;
    /** Legacy only: previous query's results at 30% opacity while loading. */
    showStaleResults: boolean;
    /** The results region (committed list, and the appended group within it). */
    showResults: boolean;
    /** The "More results" appended group (stable path only). */
    showAppended: boolean;
    /** The no-results state. */
    showEmpty: boolean;
}

/**
 * Stable-list contract (flag ON): skeletons until the ONE commit publishes the
 * list — including while streaming pre-commit and on repeat searches (the hook
 * keeps staleResults empty, so stale rows never render). A commit with zero
 * visible rows keeps the skeleton up while the stream is open (a late op may
 * still append); `done` resolves it to the empty state. phase=error drops the
 * skeleton: shimmer next to a fatal banner would promise progress that isn't
 * coming. showResults includes the appended-only case: a late op can append
 * real hits under an empty committed list.
 */
export function stableListVisibility(args: {
    hasQuery: boolean;
    phase: AiSearchPhase;
    committed: boolean;
    resultCount: number;
    hasResults: boolean;
    appendedCount: number;
}): IAiSearchVisibility {
    const { hasQuery, phase, committed, resultCount, hasResults, appendedCount } = args;
    const streamingPhases = phase === "loading" || phase === "streaming";
    const showAppended = hasQuery && appendedCount > 0;
    return {
        showSkeleton: hasQuery && streamingPhases && (!committed || resultCount === 0),
        showStaleResults: false,
        showResults: (hasQuery && hasResults) || showAppended,
        showAppended,
        showEmpty: phase === "empty",
    };
}

/**
 * Legacy contract (flag OFF): the pre-A1 behavior the T4 baselines pin —
 * skeletons only on a first search with no prior results; a repeat search
 * fades the previous results to 30% opacity while loading; no appended
 * section exists.
 */
export function legacyVisibility(args: {
    hasQuery: boolean;
    phase: AiSearchPhase;
    hasResults: boolean;
    hasStaleResults: boolean;
}): IAiSearchVisibility {
    const { hasQuery, phase, hasResults, hasStaleResults } = args;
    return {
        showSkeleton: hasQuery && phase === "loading" && !hasStaleResults,
        showStaleResults: hasQuery && phase === "loading" && hasStaleResults,
        showResults: hasQuery && hasResults,
        showAppended: false,
        showEmpty: phase === "empty",
    };
}
